import { Effect, pipe } from 'effect';

// Constants
import { EmployeeRelationshipRole, END_POINTS } from '@/constants';
import {
  DEFAULT_PROJECTS_LIMIT,
  DEFAULT_TRAININGS_LIMIT,
  STATUS_CODE,
} from '@shared/constants';

// Services
import { httpClient } from '@/services/http';

// Utils
import { handleActionError } from '@/utils/errorHandlers';

// Types
import {
  Employee,
  EmployeeProject,
  EmployeeActivitiesResponse,
  EmployeeTraining,
  RequestConfig,
  EmployeeLunchRegistration,
  EmployeeRelationship,
  EmployeeProjectResponse,
  EmployeeTrainingResponse,
  EmployeeProfile,
} from '@/types';

// Configs
import ENV from '@shared/configs';

export const getSimpleEmployeeRelationship = async (
  identity: string,
  employeeIdentity: string,
  requestConfig?: RequestConfig,
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<{
            relationships: EmployeeRelationshipRole[];
          }>(
            END_POINTS.GET_SIMPLE_EMPLOYEE_RELATIONSHIP(
              identity,
              employeeIdentity,
            ),
            requestConfig,
          );

          return response;
        },
        catch: (error) =>
          handleActionError<{ relationships: EmployeeRelationshipRole[] }>(
            error,
          ),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeInfo = async (
  username: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<Employee & { id: string }>(
            END_POINTS.GET_EMPLOYEE_INFO(username),
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => handleActionError<Employee & { id: string }>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeProfile = async (
  identity: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<EmployeeProfile>(
            END_POINTS.GET_EMPLOYEE_PROFILE(identity),
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => handleActionError<EmployeeProfile>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeProject = async (
  username: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const allProjects: EmployeeProject[] = [];
          let nextCursor: string | undefined;

          do {
            const response = await httpClient<EmployeeProjectResponse>(
              END_POINTS.GET_EMPLOYEE_PROJECT(
                username,
                DEFAULT_PROJECTS_LIMIT,
                nextCursor,
              ),
              { ...options?.requestConfig, isFullResponse: true },
            );

            if (!response?.data?.data) {
              break;
            }

            // Add all users from current page
            allProjects.push(...response.data.data);

            // Get next cursor for pagination
            const { pagination } = response.data.meta || {};

            if (
              response.data?.data &&
              response.data.data.length < DEFAULT_PROJECTS_LIMIT
            ) {
              nextCursor = undefined;
            } else {
              nextCursor = pagination?.cursor;
            }
          } while (nextCursor);

          return {
            data: allProjects,
            error: null,
            status: STATUS_CODE.OK,
          };
        },
        catch: (error) => handleActionError<EmployeeProject[]>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeActivities = async ({
  username,
  requestConfig,
  cursor = '',
}: {
  username: string;
  requestConfig?: RequestConfig;
  cursor?: string;
}) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<EmployeeActivitiesResponse>(
            END_POINTS.GET_EMPLOYEE_ACTIVITIES(username, cursor || undefined),
            { ...requestConfig, isFullResponse: true },
          );

          return response;
        },
        catch: (error) => handleActionError(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeTrainings = async (
  username: string,
  isSimple = false,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const allTrainings: EmployeeTraining[] = [];
          let nextCursor: string | undefined;

          do {
            const response = await httpClient<EmployeeTrainingResponse>(
              END_POINTS.GET_EMPLOYEE_TRAININGS(
                username,
                DEFAULT_TRAININGS_LIMIT,
                nextCursor,
                isSimple,
              ),
              { ...options?.requestConfig, isFullResponse: true },
            );

            if (!response?.data?.data) {
              break;
            }

            // Add all trainings from current page
            allTrainings.push(...response.data.data);

            // Get next cursor for pagination
            const { pagination } = response.data.meta || {};

            if (
              response.data?.data &&
              response.data.data.length < DEFAULT_TRAININGS_LIMIT
            ) {
              nextCursor = undefined;
            } else {
              nextCursor = pagination?.cursor;
            }
          } while (nextCursor);

          return {
            data: allTrainings,
            error: null,
            status: STATUS_CODE.OK,
          };
        },
        catch: (error) => handleActionError<EmployeeTraining[]>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeLunchRegistration = async (
  username: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<EmployeeLunchRegistration>(
            END_POINTS.GET_EMPLOYEE_LUNCH_REGISTRATION(username),
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => handleActionError<EmployeeLunchRegistration>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeTimeOffRequests = async (
  username: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const currentYear = new Date().getFullYear();

          const response = await httpClient<number>(
            `${END_POINTS.GET_EMPLOYEE_TIME_OFF_REQUESTS(username)}/${currentYear}`,
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => handleActionError(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeRelationships = async (
  username: string,
  options?: { requestConfig?: RequestConfig },
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<EmployeeRelationship>(
            END_POINTS.GET_EMPLOYEE_RELATIONSHIP(username),
            options?.requestConfig,
          );

          return response;
        },
        catch: (error) => handleActionError(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeYearInReviewVideo = async (
  identity: string,
  year: number,
  options?: { requestConfig?: RequestConfig },
): Promise<{ data: string | null; error: unknown; status: number }> =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const token = options?.requestConfig?.token;

          const response = await fetch(
            `${ENV.API_ENDPOINT}${END_POINTS.YEAR_IN_REVIEW_VIDEO(identity, year.toString())}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!response.ok) {
            return {
              data: null,
              error: `Failed to fetch video: ${response.statusText}`,
              status: response.status,
            };
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          return {
            data: blobUrl,
            error: null,
            status: STATUS_CODE.OK,
          };
        },
        catch: (error) => handleActionError<string>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );

export const getEmployeeYearInReviewVideoBlob = async (
  identity: string,
  year: number,
  options?: {
    requestConfig?: RequestConfig;
    onProgress?: (payload: {
      loaded: number;
      total?: number;
      percent?: number;
    }) => void;
  },
): Promise<{ data: Blob | null; error: unknown; status: number }> =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const token = options?.requestConfig?.token;

          const response = await fetch(
            `${ENV.API_ENDPOINT}${END_POINTS.YEAR_IN_REVIEW_VIDEO(identity, year.toString())}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!response.ok) {
            return {
              data: null,
              error: `Failed to fetch video: ${response.statusText}`,
              status: response.status,
            };
          }

          const contentLength = response.headers.get('content-length');
          const total = contentLength ? Number(contentLength) : undefined;

          if (!response.body) {
            const blob = await response.blob();
            options?.onProgress?.({
              loaded: total ?? blob.size,
              total,
              percent: total ? 100 : undefined,
            });

            return {
              data: blob,
              error: null,
              status: STATUS_CODE.OK,
            };
          }

          const reader = response.body.getReader();
          const chunks: BlobPart[] = [];
          let received = 0;

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;
            if (!value) continue;

            chunks.push(value);
            received += value.length;

            options?.onProgress?.({
              loaded: received,
              total,
              percent: total ? Math.round((received / total) * 100) : undefined,
            });
          }

          const blob = new Blob(chunks);

          options?.onProgress?.({
            loaded: received,
            total,
            percent: total ? 100 : undefined,
          });

          return {
            data: blob,
            error: null,
            status: STATUS_CODE.OK,
          };
        },
        catch: (error) => handleActionError<Blob>(error),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );
